import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AdminPanel({ user }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'users');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // User Management State
  const [users, setUsers] = useState([]);
  const [usersPagination, setUsersPagination] = useState({ page: 1, pageSize: 20, total: 0, totalPages: 0 });
  const [userFilters, setUserFilters] = useState({ status: '', rankId: '', search: '' });
  const [ranks, setRanks] = useState([]);
  const [roles, setRoles] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserModal, setShowUserModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Feature Toggles State
  const [toggles, setToggles] = useState([]);
  const [showToggleModal, setShowToggleModal] = useState(false);
  const [selectedToggle, setSelectedToggle] = useState(null);
  
  // Audit Logs State
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditPagination, setAuditPagination] = useState({ page: 1, pageSize: 50, total: 0, totalPages: 0 });
  const [auditFilters, setAuditFilters] = useState({ userId: '', action: '', resource: '', startDate: '', endDate: '' });
  const [availableActions, setAvailableActions] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  
  // System Stats State
  const [systemStats, setSystemStats] = useState(null);

  // Check permissions
  const canAccessAdmin = user.rank?.level >= 4;
  const isCommand = user.rank?.level === 5;

  // URL Sync - Update URL when filters/tab changes
  const updateURL = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) {
        newParams.set(key, value);
      } else {
        newParams.delete(key);
      }
    });
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Fetch Users
  const fetchUsers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', usersPagination.page);
      params.append('page_size', usersPagination.pageSize);
      if (userFilters.status) params.append('status', userFilters.status);
      if (userFilters.rankId) params.append('rank_id', userFilters.rankId);
      if (userFilters.search) params.append('search', userFilters.search);
      
      const response = await axios.get(`${API}/admin/users?${params.toString()}`);
      setUsers(response.data.users);
      setUsersPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (err) {
      console.error('Failed to fetch users:', err);
    }
  }, [usersPagination.page, usersPagination.pageSize, userFilters]);

  // Fetch Feature Toggles
  const fetchToggles = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/feature-toggles`);
      setToggles(response.data);
    } catch (err) {
      console.error('Failed to fetch toggles:', err);
    }
  }, []);

  // Fetch Audit Logs
  const fetchAuditLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', auditPagination.page);
      params.append('page_size', auditPagination.pageSize);
      if (auditFilters.userId) params.append('user_id', auditFilters.userId);
      if (auditFilters.action) params.append('action', auditFilters.action);
      if (auditFilters.resource) params.append('resource', auditFilters.resource);
      if (auditFilters.startDate) params.append('start_date', auditFilters.startDate);
      if (auditFilters.endDate) params.append('end_date', auditFilters.endDate);
      
      const response = await axios.get(`${API}/admin/audit-logs?${params.toString()}`);
      setAuditLogs(response.data.logs);
      setAuditPagination(prev => ({
        ...prev,
        total: response.data.total,
        totalPages: response.data.total_pages
      }));
    } catch (err) {
      console.error('Failed to fetch audit logs:', err);
    }
  }, [auditPagination.page, auditPagination.pageSize, auditFilters]);

  // Fetch System Stats
  const fetchSystemStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/system-stats`);
      setSystemStats(response.data);
    } catch (err) {
      console.error('Failed to fetch system stats:', err);
    }
  }, []);

  // Fetch filter options
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [ranksRes, rolesRes, actionsRes, resourcesRes] = await Promise.all([
        axios.get(`${API}/ranks`),
        axios.get(`${API}/roles`),
        axios.get(`${API}/admin/audit-logs/actions`),
        axios.get(`${API}/admin/audit-logs/resources`)
      ]);
      setRanks(ranksRes.data);
      setRoles(rolesRes.data);
      setAvailableActions(actionsRes.data);
      setAvailableResources(resourcesRes.data);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    if (!canAccessAdmin) {
      setLoading(false);
      return;
    }
    
    const loadData = async () => {
      setLoading(true);
      await fetchFilterOptions();
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAccessAdmin]);

  // Load tab-specific data
  useEffect(() => {
    if (!canAccessAdmin) return;
    
    switch (activeTab) {
      case 'users':
        fetchUsers();
        break;
      case 'toggles':
        fetchToggles();
        break;
      case 'logs':
        fetchAuditLogs();
        break;
      case 'stats':
        fetchSystemStats();
        break;
      default:
        break;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, canAccessAdmin]);

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    updateURL({ tab });
  };

  // Handle user status update
  const handleStatusUpdate = async (userId, newStatus) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/status?status=${newStatus}`);
      fetchUsers();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update status');
    }
  };

  // Handle toggle update
  const handleToggleUpdate = async (toggleId, enabled) => {
    try {
      await axios.put(`${API}/admin/feature-toggles/${toggleId}`, { enabled });
      fetchToggles();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update toggle');
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Permission denied view
  if (!canAccessAdmin) {
    return (
      <div className="space-y-6" data-testid="admin-panel-denied">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Administration Panel</h1>
          <p className="text-gray-400">System administration and control center</p>
        </div>
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-6 text-center">
          <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h3 className="text-red-300 font-bold text-xl mb-2">Access Restricted</h3>
          <p className="text-red-200">Administration access requires Commissioned Officer rank or higher.</p>
          <p className="text-red-200 mt-2">Current rank: {user.rank?.name}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white text-xl">Loading Administration Panel...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-panel">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Administration Panel</h1>
          <p className="text-gray-400">System administration, user management, and compliance</p>
        </div>
        {isCommand && (
          <span className="px-3 py-1 text-sm rounded-full bg-amber-500/20 text-amber-400 border border-amber-500">
            Command Authority
          </span>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 flex items-center justify-between">
          <span className="text-red-200">{error}</span>
          <button onClick={() => setError(null)} className="text-red-400 hover:text-red-300">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-gray-700">
        <nav className="flex space-x-8">
          {[
            { id: 'users', label: 'User Management', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
            { id: 'toggles', label: 'Feature Toggles', icon: 'M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4' },
            { id: 'logs', label: 'Audit Logs', icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2' },
            { id: 'stats', label: 'System Stats', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z' }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`flex items-center px-1 py-4 border-b-2 font-medium text-sm transition-colors ${
                activeTab === tab.id
                  ? 'border-amber-500 text-amber-400'
                  : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-500'
              }`}
              data-testid={`tab-${tab.id}`}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={tab.icon} />
              </svg>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-4" data-testid="users-tab">
            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <input
                  type="text"
                  placeholder="Search name, email, service number..."
                  value={userFilters.search}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="users-search"
                />
                <select
                  value={userFilters.status}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="users-status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="Active">Active</option>
                  <option value="Reserve">Reserve</option>
                  <option value="Suspended">Suspended</option>
                  <option value="Discharged">Discharged</option>
                  <option value="On Leave">On Leave</option>
                </select>
                <select
                  value={userFilters.rankId}
                  onChange={(e) => setUserFilters(prev => ({ ...prev, rankId: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="users-rank-filter"
                >
                  <option value="">All Ranks</option>
                  {ranks.map(rank => (
                    <option key={rank.id} value={rank.id}>{rank.name}</option>
                  ))}
                </select>
                <button
                  onClick={() => {
                    setUserFilters({ status: '', rankId: '', search: '' });
                    setUsersPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white transition-colors"
                >
                  Clear Filters
                </button>
              </div>
              <div className="flex justify-between items-center mt-4">
                <button
                  onClick={fetchUsers}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium transition-colors"
                  data-testid="users-apply-filters"
                >
                  Apply Filters
                </button>
                {isCommand && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-medium transition-colors"
                    data-testid="create-user-btn"
                  >
                    + Create User
                  </button>
                )}
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Personnel</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Department</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-300 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {users.map(u => (
                    <tr key={u.id} className="hover:bg-gray-700/50" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-3">
                        <div>
                          <p className="text-white font-medium">{u.full_name}</p>
                          <p className="text-gray-400 text-sm">{u.service_number}</p>
                          <p className="text-gray-500 text-xs">{u.email}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.rank?.level === 5 ? 'bg-amber-500/20 text-amber-400' :
                          u.rank?.level === 4 ? 'bg-purple-500/20 text-purple-400' :
                          u.rank?.level === 3 ? 'bg-blue-500/20 text-blue-400' :
                          u.rank?.level === 2 ? 'bg-green-500/20 text-green-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {u.rank?.name}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.status === 'Active' ? 'bg-green-500/20 text-green-400' :
                          u.status === 'Suspended' ? 'bg-red-500/20 text-red-400' :
                          u.status === 'Reserve' ? 'bg-blue-500/20 text-blue-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {u.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-300">{u.department || '-'}</td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          <button
                            onClick={() => { setSelectedUser(u); setShowUserModal(true); }}
                            className="px-3 py-1 bg-blue-600 hover:bg-blue-500 rounded text-white text-sm transition-colors"
                            data-testid={`edit-user-${u.id}`}
                          >
                            Edit
                          </button>
                          {u.rank?.level < user.rank?.level && u.status !== 'Suspended' && (
                            <button
                              onClick={() => handleStatusUpdate(u.id, 'Suspended')}
                              className="px-3 py-1 bg-red-600 hover:bg-red-500 rounded text-white text-sm transition-colors"
                            >
                              Suspend
                            </button>
                          )}
                          {u.rank?.level < user.rank?.level && u.status === 'Suspended' && (
                            <button
                              onClick={() => handleStatusUpdate(u.id, 'Active')}
                              className="px-3 py-1 bg-green-600 hover:bg-green-500 rounded text-white text-sm transition-colors"
                            >
                              Activate
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {/* Pagination */}
              <div className="bg-gray-700 px-4 py-3 flex items-center justify-between">
                <div className="text-gray-400 text-sm">
                  Showing {((usersPagination.page - 1) * usersPagination.pageSize) + 1} to {Math.min(usersPagination.page * usersPagination.pageSize, usersPagination.total)} of {usersPagination.total}
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setUsersPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={usersPagination.page <= 1}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <span className="px-3 py-1 text-white">Page {usersPagination.page} of {usersPagination.totalPages || 1}</span>
                  <button
                    onClick={() => setUsersPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={usersPagination.page >= usersPagination.totalPages}
                    className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Feature Toggles Tab */}
        {activeTab === 'toggles' && (
          <div className="space-y-4" data-testid="toggles-tab">
            {!isCommand && (
              <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4">
                <p className="text-yellow-200">View-only access. Command rank required to modify toggles.</p>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {toggles.map(toggle => (
                <div
                  key={toggle.id}
                  className={`bg-gray-800 rounded-lg p-4 border ${toggle.enabled ? 'border-green-700' : 'border-red-700'}`}
                  data-testid={`toggle-${toggle.feature_key}`}
                >
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-white font-medium">{toggle.feature_name}</h3>
                      <p className="text-gray-400 text-sm mt-1">{toggle.description}</p>
                      <div className="flex items-center space-x-2 mt-2">
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          toggle.toggle_type === 'Module' ? 'bg-blue-500/20 text-blue-400' :
                          toggle.toggle_type === 'Feature' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-gray-500/20 text-gray-400'
                        }`}>
                          {toggle.toggle_type}
                        </span>
                        <span className="text-gray-500 text-xs">Min Rank: Level {toggle.minimum_rank_level}</span>
                      </div>
                    </div>
                    <div className="flex items-center">
                      {isCommand ? (
                        <button
                          onClick={() => handleToggleUpdate(toggle.id, !toggle.enabled)}
                          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                            toggle.enabled ? 'bg-green-600' : 'bg-red-600'
                          }`}
                          data-testid={`toggle-switch-${toggle.feature_key}`}
                        >
                          <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            toggle.enabled ? 'translate-x-6' : 'translate-x-1'
                          }`} />
                        </button>
                      ) : (
                        <span className={`px-3 py-1 rounded text-sm ${
                          toggle.enabled ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
                        }`}>
                          {toggle.enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {isCommand && (
              <button
                onClick={() => setShowToggleModal(true)}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium transition-colors"
                data-testid="create-toggle-btn"
              >
                + Create Feature Toggle
              </button>
            )}
          </div>
        )}

        {/* Audit Logs Tab */}
        {activeTab === 'logs' && (
          <div className="space-y-4" data-testid="logs-tab">
            {/* Filters */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <select
                  value={auditFilters.action}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, action: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="logs-action-filter"
                >
                  <option value="">All Actions</option>
                  {availableActions.map(action => (
                    <option key={action} value={action}>{action.replace(/_/g, ' ')}</option>
                  ))}
                </select>
                <select
                  value={auditFilters.resource}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, resource: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="logs-resource-filter"
                >
                  <option value="">All Resources</option>
                  {availableResources.map(resource => (
                    <option key={resource} value={resource}>{resource}</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="User ID"
                  value={auditFilters.userId}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, userId: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="logs-user-filter"
                />
                <input
                  type="date"
                  value={auditFilters.startDate}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, startDate: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="logs-start-date"
                />
                <input
                  type="date"
                  value={auditFilters.endDate}
                  onChange={(e) => setAuditFilters(prev => ({ ...prev, endDate: e.target.value }))}
                  className="px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
                  data-testid="logs-end-date"
                />
                <button
                  onClick={() => {
                    setAuditFilters({ userId: '', action: '', resource: '', startDate: '', endDate: '' });
                    setAuditPagination(prev => ({ ...prev, page: 1 }));
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white transition-colors"
                >
                  Clear
                </button>
              </div>
              <div className="mt-4">
                <button
                  onClick={fetchAuditLogs}
                  className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium transition-colors"
                  data-testid="logs-apply-filters"
                >
                  Apply Filters
                </button>
              </div>
            </div>

            {/* Immutable Notice */}
            <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-3 flex items-center">
              <svg className="w-5 h-5 text-yellow-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              <span className="text-yellow-200 text-sm">All audit logs are immutable and legally admissible records</span>
            </div>

            {/* Logs List */}
            <div className="space-y-2">
              {auditLogs.map(log => (
                <div key={log.id} className="bg-gray-800 rounded-lg p-4 border border-gray-700 hover:border-gray-600 transition-colors" data-testid={`log-${log.id}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="px-2 py-1 bg-blue-500/20 text-blue-400 rounded text-xs font-medium">
                          {log.action.replace(/_/g, ' ').toUpperCase()}
                        </span>
                        <span className="px-2 py-1 bg-gray-600 text-gray-300 rounded text-xs">
                          {log.resource}
                        </span>
                      </div>
                      <div className="mt-2 text-sm text-gray-400">
                        <span className="text-white">{log.user_name || log.user_id}</span>
                        {log.service_number && <span className="ml-2 text-gray-500">({log.service_number})</span>}
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-2 p-2 bg-gray-900 rounded text-xs font-mono text-gray-400 max-h-24 overflow-auto">
                          {JSON.stringify(log.details, null, 2)}
                        </div>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <p className="text-gray-400 text-xs">{formatTimestamp(log.timestamp)}</p>
                      {log.ip_address && <p className="text-gray-500 text-xs mt-1">IP: {log.ip_address}</p>}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="bg-gray-800 rounded-lg px-4 py-3 flex items-center justify-between border border-gray-700">
              <div className="text-gray-400 text-sm">
                Total: {auditPagination.total} logs
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setAuditPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                  disabled={auditPagination.page <= 1}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-white">Page {auditPagination.page} of {auditPagination.totalPages || 1}</span>
                <button
                  onClick={() => setAuditPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={auditPagination.page >= auditPagination.totalPages}
                  className="px-3 py-1 bg-gray-600 hover:bg-gray-500 rounded text-white disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}

        {/* System Stats Tab */}
        {activeTab === 'stats' && systemStats && (
          <div className="space-y-6" data-testid="stats-tab">
            {/* Personnel Overview */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <p className="text-gray-400 text-sm">Total Personnel</p>
                <p className="text-3xl font-bold text-white mt-1">{systemStats.personnel.total}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-green-700">
                <p className="text-gray-400 text-sm">Active</p>
                <p className="text-3xl font-bold text-green-400 mt-1">{systemStats.personnel.active}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-red-700">
                <p className="text-gray-400 text-sm">Suspended</p>
                <p className="text-3xl font-bold text-red-400 mt-1">{systemStats.personnel.suspended}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-4 border border-amber-700">
                <p className="text-gray-400 text-sm">Audit Logs Today</p>
                <p className="text-3xl font-bold text-amber-400 mt-1">{systemStats.audit_logs.today}</p>
              </div>
            </div>

            {/* Status Distribution */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-white font-medium mb-4">Personnel by Status</h3>
                <div className="space-y-3">
                  {Object.entries(systemStats.personnel.status_distribution).map(([status, count]) => (
                    <div key={status} className="flex justify-between items-center">
                      <span className="text-gray-300">{status}</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-700 rounded-full h-2 mr-3">
                          <div
                            className={`h-2 rounded-full ${
                              status === 'Active' ? 'bg-green-500' :
                              status === 'Suspended' ? 'bg-red-500' :
                              'bg-gray-500'
                            }`}
                            style={{ width: `${(count / systemStats.personnel.total) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                <h3 className="text-white font-medium mb-4">Personnel by Rank</h3>
                <div className="space-y-3">
                  {Object.entries(systemStats.personnel.rank_distribution).map(([rank, count]) => (
                    <div key={rank} className="flex justify-between items-center">
                      <span className="text-gray-300">{rank}</span>
                      <div className="flex items-center">
                        <div className="w-32 bg-gray-700 rounded-full h-2 mr-3">
                          <div
                            className={`h-2 rounded-full ${
                              rank === 'Command' ? 'bg-amber-500' :
                              rank === 'Commissioned Officer' ? 'bg-purple-500' :
                              rank === 'Warrant' ? 'bg-blue-500' :
                              rank === 'NCO' ? 'bg-green-500' :
                              'bg-gray-500'
                            }`}
                            style={{ width: `${(count / systemStats.personnel.total) * 100}%` }}
                          />
                        </div>
                        <span className="text-white font-medium">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-white font-medium mb-4">Last 24 Hours Activity</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-gray-700 rounded-lg">
                  <p className="text-2xl font-bold text-blue-400">{systemStats.recent_activity.logins_24h}</p>
                  <p className="text-gray-400 text-sm mt-1">Logins</p>
                </div>
                <div className="text-center p-4 bg-gray-700 rounded-lg">
                  <p className="text-2xl font-bold text-purple-400">{systemStats.recent_activity.personnel_changes_24h}</p>
                  <p className="text-gray-400 text-sm mt-1">Personnel Changes</p>
                </div>
                <div className="text-center p-4 bg-gray-700 rounded-lg">
                  <p className="text-2xl font-bold text-amber-400">{systemStats.audit_logs.total}</p>
                  <p className="text-gray-400 text-sm mt-1">Total Audit Logs</p>
                </div>
              </div>
            </div>

            {/* Feature Toggles Summary */}
            <div className="bg-gray-800 rounded-lg p-4 border border-gray-700">
              <h3 className="text-white font-medium mb-4">Feature Toggles</h3>
              <div className="flex items-center space-x-6">
                <div>
                  <span className="text-gray-400 text-sm">Total Toggles</span>
                  <p className="text-xl font-bold text-white">{systemStats.feature_toggles.total}</p>
                </div>
                <div>
                  <span className="text-gray-400 text-sm">Disabled</span>
                  <p className="text-xl font-bold text-red-400">{systemStats.feature_toggles.disabled}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* User Edit Modal */}
      {showUserModal && selectedUser && (
        <UserEditModal
          user={selectedUser}
          ranks={ranks}
          roles={roles}
          currentUserRank={user.rank?.level}
          onClose={() => { setShowUserModal(false); setSelectedUser(null); }}
          onSave={() => { setShowUserModal(false); setSelectedUser(null); fetchUsers(); }}
        />
      )}

      {/* Create User Modal */}
      {showCreateModal && (
        <CreateUserModal
          ranks={ranks}
          roles={roles}
          currentUserRank={user.rank?.level}
          onClose={() => setShowCreateModal(false)}
          onSave={() => { setShowCreateModal(false); fetchUsers(); }}
        />
      )}

      {/* Create Toggle Modal */}
      {showToggleModal && (
        <CreateToggleModal
          onClose={() => setShowToggleModal(false)}
          onSave={() => { setShowToggleModal(false); fetchToggles(); }}
        />
      )}
    </div>
  );
}

// User Edit Modal Component
function UserEditModal({ user: targetUser, ranks, roles, currentUserRank, onClose, onSave }) {
  const [formData, setFormData] = useState({
    email: targetUser.email || '',
    full_name: targetUser.full_name || '',
    status: targetUser.status || 'Active',
    department: targetUser.department || '',
    role_ids: targetUser.role_ids || [],
    deployment_eligible: targetUser.deployment_eligible ?? true,
    medical_clearance: targetUser.medical_clearance ?? false,
    weapons_clearance: targetUser.weapons_clearance ?? false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.put(`${API}/admin/users/${targetUser.id}`, formData);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setLoading(false);
    }
  };

  const canModify = targetUser.rank?.level < currentUserRank;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Edit Personnel: {targetUser.full_name}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!canModify && (
          <div className="bg-yellow-900/30 border border-yellow-700 rounded-lg p-4 mb-4">
            <p className="text-yellow-200">View-only. Cannot modify users of equal or higher rank.</p>
          </div>
        )}

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Full Name</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                disabled={!canModify}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                disabled={!canModify}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white disabled:opacity-50"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                disabled={!canModify}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white disabled:opacity-50"
              >
                <option value="Active">Active</option>
                <option value="Reserve">Reserve</option>
                <option value="Suspended">Suspended</option>
                <option value="Discharged">Discharged</option>
                <option value="On Leave">On Leave</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                disabled={!canModify}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white disabled:opacity-50"
              >
                <option value="">Select Department</option>
                <option value="Command">Command</option>
                <option value="Operations">Operations</option>
                <option value="Training">Training</option>
                <option value="Finance">Finance</option>
                <option value="Admin">Admin</option>
                <option value="Intelligence">Intelligence</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Roles</label>
            <div className="grid grid-cols-3 gap-2">
              {roles.map(role => (
                <label key={role.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.role_ids.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, role_ids: [...prev.role_ids, role.id] }));
                      } else {
                        setFormData(prev => ({ ...prev, role_ids: prev.role_ids.filter(id => id !== role.id) }));
                      }
                    }}
                    disabled={!canModify}
                    className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-gray-300 text-sm">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.deployment_eligible}
                onChange={(e) => setFormData(prev => ({ ...prev, deployment_eligible: e.target.checked }))}
                disabled={!canModify}
                className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-gray-300 text-sm">Deployment Eligible</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.medical_clearance}
                onChange={(e) => setFormData(prev => ({ ...prev, medical_clearance: e.target.checked }))}
                disabled={!canModify}
                className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-gray-300 text-sm">Medical Clearance</span>
            </label>
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                checked={formData.weapons_clearance}
                onChange={(e) => setFormData(prev => ({ ...prev, weapons_clearance: e.target.checked }))}
                disabled={!canModify}
                className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
              />
              <span className="text-gray-300 text-sm">Weapons Clearance</span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white"
            >
              Cancel
            </button>
            {canModify && (
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// Create User Modal Component
function CreateUserModal({ ranks, roles, currentUserRank, onClose, onSave }) {
  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    full_name: '',
    rank_id: '',
    role_ids: [],
    department: '',
    status: 'Active'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const availableRanks = ranks.filter(r => r.level < currentUserRank);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/admin/users`, formData);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Create New Personnel</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Username *</label>
              <input
                type="text"
                value={formData.username}
                onChange={(e) => setFormData(prev => ({ ...prev, username: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Full Name *</label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Password *</label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                required
                placeholder="Min 8 chars, upper, lower, number, special"
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Rank *</label>
              <select
                value={formData.rank_id}
                onChange={(e) => setFormData(prev => ({ ...prev, rank_id: e.target.value }))}
                required
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">Select Rank</option>
                {availableRanks.map(rank => (
                  <option key={rank.id} value={rank.id}>{rank.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Department</label>
              <select
                value={formData.department}
                onChange={(e) => setFormData(prev => ({ ...prev, department: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="">Select Department</option>
                <option value="Operations">Operations</option>
                <option value="Training">Training</option>
                <option value="Finance">Finance</option>
                <option value="Admin">Admin</option>
                <option value="Intelligence">Intelligence</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-2">Roles</label>
            <div className="grid grid-cols-3 gap-2">
              {roles.map(role => (
                <label key={role.id} className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.role_ids.includes(role.id)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData(prev => ({ ...prev, role_ids: [...prev.role_ids, role.id] }));
                      } else {
                        setFormData(prev => ({ ...prev, role_ids: prev.role_ids.filter(id => id !== role.id) }));
                      }
                    }}
                    className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
                  />
                  <span className="text-gray-300 text-sm">{role.name}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-green-600 hover:bg-green-500 rounded-lg text-white font-medium disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Personnel'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Create Toggle Modal Component
function CreateToggleModal({ onClose, onSave }) {
  const [formData, setFormData] = useState({
    feature_key: '',
    feature_name: '',
    description: '',
    toggle_type: 'Feature',
    enabled: true,
    minimum_rank_level: 1,
    affected_modules: []
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      await axios.post(`${API}/admin/feature-toggles`, formData);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to create toggle');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-gray-800 rounded-lg p-6 w-full max-w-lg border border-gray-700">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-white">Create Feature Toggle</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700 rounded-lg p-4 mb-4">
            <p className="text-red-200">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-gray-400 text-sm mb-1">Feature Key *</label>
            <input
              type="text"
              value={formData.feature_key}
              onChange={(e) => setFormData(prev => ({ ...prev, feature_key: e.target.value.toLowerCase().replace(/\s+/g, '_') }))}
              required
              placeholder="e.g., new_feature_name"
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">Feature Name *</label>
            <input
              type="text"
              value={formData.feature_name}
              onChange={(e) => setFormData(prev => ({ ...prev, feature_name: e.target.value }))}
              required
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div>
            <label className="block text-gray-400 text-sm mb-1">Description *</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              required
              rows={2}
              className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-400 text-sm mb-1">Toggle Type</label>
              <select
                value={formData.toggle_type}
                onChange={(e) => setFormData(prev => ({ ...prev, toggle_type: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="Module">Module</option>
                <option value="Feature">Feature</option>
                <option value="Capability">Capability</option>
              </select>
            </div>
            <div>
              <label className="block text-gray-400 text-sm mb-1">Min Rank Level</label>
              <select
                value={formData.minimum_rank_level}
                onChange={(e) => setFormData(prev => ({ ...prev, minimum_rank_level: parseInt(e.target.value) }))}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white"
              >
                <option value="1">1 - Enlisted</option>
                <option value="2">2 - NCO</option>
                <option value="3">3 - Warrant</option>
                <option value="4">4 - Commissioned Officer</option>
                <option value="5">5 - Command</option>
              </select>
            </div>
          </div>

          <label className="flex items-center space-x-2">
            <input
              type="checkbox"
              checked={formData.enabled}
              onChange={(e) => setFormData(prev => ({ ...prev, enabled: e.target.checked }))}
              className="w-4 h-4 rounded border-gray-600 text-amber-500 focus:ring-amber-500"
            />
            <span className="text-gray-300">Enabled by default</span>
          </label>

          <div className="flex justify-end space-x-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 hover:bg-gray-500 rounded-lg text-white"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-amber-600 hover:bg-amber-500 rounded-lg text-white font-medium disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create Toggle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AdminPanel;
